import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";

const DEFAULT_OG_IMAGE =
 "https://ljmlvdnpqjclotvrydqu.supabase.co/storage/v1/object/public/cms-assets/og/home-1779203883263.png";
const SITE_NAME = "Nil Beauty";
const TWITTER_HANDLE = "@nilbeautyofficial";

interface SEOHeadProps {
 title: string;
 description: string;
 canonical?: string;
 ogImage?: string;
 ogImageAlt?: string;
 ogType?: string;
 keywords?: string;
 jsonLd?: object | object[];
 noindex?: boolean;
}

const SEOHead = ({ title, description, canonical, ogImage, ogImageAlt, ogType = "website", keywords, jsonLd, noindex = false }: SEOHeadProps) => {
 const { lang } = useLanguage();


 useEffect(() => {
 // Title
 document.title = title;

 // Helper to set/create meta
 const setMeta = (attr: string, attrValue: string, content: string) => {
 let el = document.querySelector(`meta[${attr}="${attrValue}"]`) as HTMLMetaElement | null;
 if (!el) {
 el = document.createElement("meta");
 el.setAttribute(attr, attrValue);
 document.head.appendChild(el);
 }
 el.setAttribute("content", content);
 };

 setMeta("name", "description", description);
 if (keywords) setMeta("name", "keywords", keywords);

 // Open Graph, core
 setMeta("property", "og:site_name", SITE_NAME);
 setMeta("property", "og:title", title);
 setMeta("property", "og:description", description);
 setMeta("property", "og:type", ogType);
 setMeta("property", "og:locale", lang === "ar" ? "ar_EG" : "en_US");
 setMeta("property", "og:locale:alternate", lang === "ar" ? "en_US" : "ar_EG");
 if (canonical) setMeta("property", "og:url", canonical);

 // Twitter Card
 setMeta("name", "twitter:card", "summary_large_image");
 setMeta("name", "twitter:site", TWITTER_HANDLE);
 setMeta("name", "twitter:creator", TWITTER_HANDLE);
 setMeta("name", "twitter:title", title);
 setMeta("name", "twitter:description", description);

 // Robots directive, noindex/nofollow for transactional & account routes
 setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
 setMeta("name", "googlebot", noindex ? "noindex, nofollow" : "index, follow");

 // Image, use per-page image if provided, else fall back to site default so
 // every shared link renders a preview card.
 const imageUrl = ogImage || DEFAULT_OG_IMAGE;
 const imageAlt = ogImageAlt || title;
 setMeta("property", "og:image", imageUrl);
 setMeta("property", "og:image:secure_url", imageUrl);
 setMeta("property", "og:image:alt", imageAlt);
 setMeta("name", "twitter:image", imageUrl);
 setMeta("name", "twitter:image:alt", imageAlt);



 // Canonical
 let canonicalEl = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
 if (canonical) {
 if (!canonicalEl) {
 canonicalEl = document.createElement("link");
 canonicalEl.setAttribute("rel", "canonical");
 document.head.appendChild(canonicalEl);
 }
 canonicalEl.setAttribute("href", canonical);
 }

 // hreflang alternates (en / ar / x-default), bilingual SEO
 try {
 const base =
 canonical ||
 (typeof window !== "undefined"
 ? `https://www.nilbeautyofficial.com${window.location.pathname}`
 : "");
 if (base) {
 const url = new URL(base);
 const buildAlt = (lang: string | null) => {
 const u = new URL(url.toString());
 if (lang) u.searchParams.set("lang", lang);
 else u.searchParams.delete("lang");
 return u.toString();
 };
 const alternates: Array<[string, string]> = [
 ["en", buildAlt("en")],
 ["ar", buildAlt("ar")],
 ["x-default", buildAlt(null)],
 ];
 document
 .querySelectorAll("link[rel='alternate'][data-seo-hreflang]")
 .forEach((n) => n.remove());
 alternates.forEach(([hl, href]) => {
 const link = document.createElement("link");
 link.setAttribute("rel", "alternate");
 link.setAttribute("hreflang", hl);
 link.setAttribute("href", href);
 link.setAttribute("data-seo-hreflang", "true");
 document.head.appendChild(link);
 });
 }
 } catch {
 // ignore malformed URLs
 }

 // JSON-LD
 const existingScripts = document.querySelectorAll('script[data-seo-jsonld]');
 existingScripts.forEach((s) => s.remove());


 if (jsonLd) {
 const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
 items.forEach((item) => {
 const script = document.createElement("script");
 script.type = "application/ld+json";
 script.setAttribute("data-seo-jsonld", "true");
 script.textContent = JSON.stringify(item);
 document.head.appendChild(script);
 });
 }

 return () => {
 const scripts = document.querySelectorAll('script[data-seo-jsonld]');
 scripts.forEach((s) => s.remove());
 };
 }, [title, description, canonical, ogImage, ogImageAlt, ogType, keywords, jsonLd, noindex, lang]);

 return null;
};

export default SEOHead;
